// src/screens/CompleteAccountSetup.js
// ✅ DROP-IN: CompleteAccountSetup that uses ONLY company.seatLimits for seat limits (single source of truth)
// - Trial sets company.seatLimits = selected seats
// - Stripe checkout writes company.seatLimits BEFORE redirect (so values survive the redirect and are consistent)
// - No reads/writes of adminSeats/endUserSeats/trialAdminSeats/trialEndUserSeats/seats.purchased/gift seats, etc.

import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { auth, db } from "../firebase";
import { postFunctionJson } from "../utils/functionHttp";
import {
  doc,
  setDoc,
  updateDoc,
  serverTimestamp,
  getDoc,
  Timestamp,
  arrayUnion,
} from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { signOut } from "firebase/auth";
import {
  cacheUserProfile,
  getCachedUserProfile,
  getAppBasePath,
  setPostSetupRouteOverride,
} from "../auth/authState";

// ---------------------------------------------
// Constants: company types & product specialties
// ---------------------------------------------
const COMPANY_TYPES = [
  "Broadline Foodservice Distributor",
  "Specialty Food Distributor",
  "Meat & Protein Distributor",
  "Seafood Distributor",
  "Produce Distributor",
  "Beverage Distributor (Non-Alcoholic)",
  "Wine, Beer & Spirits Distributor",
  "Dairy & Frozen Foods Distributor",
  "Food Manufacturer / Brand",
  "Importer / Broker / Manufacturer Rep",
  "Other",
];

const COMPANY_SPECIALTIES = [
  "Broadline / General Foodservice",
  "Seafood",
  "Beef",
  "Poultry",
  "Pork",
  "Cheese & Dairy",
  "Bakery & Desserts",
  "Coffee & Tea",
  "Soft Drinks & Soda",
  "Juice & Smoothies",
  "Water & Hydration",
  "Snacks & Chips",
  "Sauces & Condiments",
  "Spices & Seasonings",
  "Breakfast Items",
  "Ethnic / Global Cuisine",
  "Frozen Prepared Foods",
  "Ready-to-Eat / Grab & Go",
  "Plant-Based / Vegetarian",
  "Deli Meats & Charcuterie",
  "Health / Wellness / Better-for-You",
  "Organic / Natural",
  "Gluten-Free / Allergen-Sensitive",
  "Bar Mixes & Cocktail Ingredients",
  "Non-Food / Disposables (cups, to-go, etc.)",
];

// ---------------------------------------------
// System groups (deterministic IDs)
// ---------------------------------------------
const ORG_ROOT = "companies";
const GROUP_ALL_STAFF = "grp_all_staff";
const GROUP_INACTIVE_STAFF = "grp_inactive_staff";
const GROUP_DELIVERY_STAFF = "grp_delivery_staff";
const GROUP_WAREHOUSE_STAFF = "grp_warehouse_staff";

// -----------------------------
// SeatLimits helpers (ONLY seatLimits)
// -----------------------------
const parseSeat = (v) => {
  if (v === null || v === undefined) return null;
  if (typeof v === "number" && Number.isFinite(v) && v >= 0) return v;
  if (typeof v === "string" && v.trim() !== "") {
    const n = parseInt(v, 10);
    return Number.isFinite(n) && n >= 0 ? n : null;
  }
  return null;
};

const clampSeat = (n, min) => {
  const v = typeof n === "number" ? n : parseInt(n, 10);
  if (!Number.isFinite(v)) return min;
  return Math.max(min, v);
};

const SETUP_SNAPSHOT_STORAGE_KEY = "completeAccountSetupSnapshot";

const readSetupSnapshot = () => {
  try {
    const raw = window.sessionStorage.getItem(SETUP_SNAPSHOT_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (err) {
    console.warn("[Setup] Unable to read setup snapshot", err);
    return null;
  }
};

const writeSetupSnapshot = (snapshot) => {
  try {
    window.sessionStorage.setItem(SETUP_SNAPSHOT_STORAGE_KEY, JSON.stringify(snapshot));
  } catch (err) {
    console.warn("[Setup] Unable to write setup snapshot", err);
  }
};

const clearSetupSnapshot = () => {
  try {
    window.sessionStorage.removeItem(SETUP_SNAPSHOT_STORAGE_KEY);
  } catch (err) {
    console.warn("[Setup] Unable to clear setup snapshot", err);
  }
};

// 🔄 Helper: keep App.js in sync by updating the same uid-scoped cache it reads for routing
const updateLocalUserProfile = (patch) => {
  try {
    const uid = auth.currentUser?.uid || patch?.uid || null;
    const storageBase = uid ? getCachedUserProfile(uid) : null;

    let rawBase = {};
    try {
      const raw = window.localStorage.getItem("userProfile");
      rawBase = raw ? JSON.parse(raw) : {};
    } catch (parseErr) {
      console.warn("[Setup] Unable to parse userProfile from localStorage", parseErr);
    }

    const merged = {
      ...rawBase,
      ...(storageBase || {}),
      ...patch,
      ...(uid ? { uid } : {}),
    };

    cacheUserProfile(merged);
  } catch (err) {
    console.warn("[Setup] Unable to update userProfile in localStorage", err);
  }
};

// ✅ Derive full name from known sources (OAuth displayName, user doc, etc.)
const deriveFullName = ({ user, userData }) => {
  const fromDoc = (userData?.fullName || "").trim();
  if (fromDoc) return fromDoc;

  const fromAuth = (user?.displayName || "").trim();
  if (fromAuth) return fromAuth;

  const fromParts = [userData?.firstName, userData?.lastName].filter(Boolean).join(" ").trim();
  return fromParts || "";
};

// ✅ Never allow company name to prefill with the user's full name (or placeholders)
const sanitizeCompanyName = (candidate, fullName) => {
  const c = (candidate || "").trim();
  if (!c) return "";

  const lower = c.toLowerCase();
  const fullLower = (fullName || "").trim().toLowerCase();

  const placeholders = new Set(["new company", "company", "my company"]);
  if (placeholders.has(lower)) return "";

  if (fullLower && lower === fullLower) return "";
  return c;
};

// ✅ Ensure system groups exist (idempotent, deterministic doc IDs) - WITH ERROR HANDLING
const ensureSystemGroups = async ({ companyId, ownerUid }) => {
  if (!companyId || !ownerUid) {
    console.warn("[Setup] ensureSystemGroups: missing companyId or ownerUid");
    return;
  }

  // ✅ CRITICAL SAFETY CHECK - Prevent uid==companyId bug
  if (companyId === ownerUid) {
    console.error("[Setup] ❌ CRITICAL: Attempted to create system groups with companyId === uid");
    throw new Error(
      "Invalid company configuration detected (companyId = uid). " +
        "Please contact support immediately. DO NOT PROCEED."
    );
  }

  console.log("[Setup] Ensuring system groups for companyId:", companyId, "ownerUid:", ownerUid);

  const companyRef = doc(db, ORG_ROOT, companyId);

  try {
    const companySnap = await getDoc(companyRef);

    if (!companySnap.exists()) {
      try {
        await setDoc(
          companyRef,
          {
            companyId,
            ownerUid,
            name: "New Company",
            defaultVisibility: "groups",
            defaultGroupIds: [GROUP_ALL_STAFF],
            createdAt: serverTimestamp(),
            createdBy: ownerUid,
            updatedAt: serverTimestamp(),
          },
          { merge: true }
        );
        console.log("[Setup] ✅ Created company doc:", companyId);
      } catch (createErr) {
        console.warn("[Setup] Could not create company doc (may already exist):", createErr.message);
      }
    } else {
      console.log("[Setup] Company doc already exists:", companyId);
    }
  } catch (err) {
    console.warn("[Setup] ⚠️ Could not read company doc (permissions), but continuing:", err.message);
  }

  // Try to create groups, but don't fail if we can't
  try {
    const allStaffRef = doc(db, ORG_ROOT, companyId, "groups", GROUP_ALL_STAFF);
    const inactiveStaffRef = doc(db, ORG_ROOT, companyId, "groups", GROUP_INACTIVE_STAFF);
    const deliveryStaffRef = doc(db, ORG_ROOT, companyId, "groups", GROUP_DELIVERY_STAFF);
    const warehouseStaffRef = doc(db, ORG_ROOT, companyId, "groups", GROUP_WAREHOUSE_STAFF);

    const [allStaffSnap, inactiveSnap, deliverySnap, warehouseSnap] = await Promise.all([
      getDoc(allStaffRef).catch(() => ({ exists: () => false })),
      getDoc(inactiveStaffRef).catch(() => ({ exists: () => false })),
      getDoc(deliveryStaffRef).catch(() => ({ exists: () => false })),
      getDoc(warehouseStaffRef).catch(() => ({ exists: () => false })),
    ]);

    if (!allStaffSnap.exists()) {
      try {
        await setDoc(
          allStaffRef,
          {
            name: "All Staff",
            description: "Default group that includes all internal staff users.",
            isSystem: true,
            systemKey: "grp_all_staff",
            createdAt: serverTimestamp(),
            createdBy: ownerUid,
            updatedAt: serverTimestamp(),
            updatedBy: ownerUid,
          },
          { merge: true }
        );
        console.log("[Setup] ✅ Created all_staff group");
      } catch (err) {
        console.warn("[Setup] Could not create all_staff group:", err.message);
      }
    }

    if (!inactiveSnap.exists()) {
      try {
        await setDoc(
          inactiveStaffRef,
          {
            name: "Inactive Staff",
            description:
              "System group for staff who are inactive. Used for admin filtering/housekeeping (not visibility).",
            isSystem: true,
            systemKey: "inactive_staff",
            createdAt: serverTimestamp(),
            createdBy: ownerUid,
            updatedAt: serverTimestamp(),
            updatedBy: ownerUid,
          },
          { merge: true }
        );
        console.log("[Setup] ✅ Created inactive_staff group");
      } catch (err) {
        console.warn("[Setup] Could not create inactive_staff group:", err.message);
      }
    }

    if (!deliverySnap.exists()) {
      try {
        await setDoc(
          deliveryStaffRef,
          {
            name: "Delivery Staff",
            description: "Admin-only operational group. Members can only move sample orders from Shipped to Delivered.",
            isSystem: true,
            systemKey: "delivery_staff",
            createdAt: serverTimestamp(),
            createdBy: ownerUid,
            updatedAt: serverTimestamp(),
            updatedBy: ownerUid,
          },
          { merge: true }
        );
        console.log("[Setup] ✅ Created delivery_staff group");
      } catch (err) {
        console.warn("[Setup] Could not create delivery_staff group:", err.message);
      }
    }

    if (!warehouseSnap.exists()) {
      try {
        await setDoc(
          warehouseStaffRef,
          {
            name: "Warehouse Staff",
            description: "Admin-only operational group. Members can only move sample orders from Approved to Preparation or Shipped.",
            isSystem: true,
            systemKey: "warehouse_staff",
            createdAt: serverTimestamp(),
            createdBy: ownerUid,
            updatedAt: serverTimestamp(),
            updatedBy: ownerUid,
          },
          { merge: true }
        );
        console.log("[Setup] ✅ Created warehouse_staff group");
      } catch (err) {
        console.warn("[Setup] Could not create warehouse_staff group:", err.message);
      }
    }

    // Add user as member
    try {
      const memberRef = doc(db, ORG_ROOT, companyId, "groups", GROUP_ALL_STAFF, "members", ownerUid);
      await setDoc(
        memberRef,
        {
          uid: ownerUid,
          role: "owner",
          joinedAt: serverTimestamp(),
          addedBy: ownerUid,
        },
        { merge: true }
      );
    } catch (err) {
      console.warn("[Setup] Could not add user to group members:", err.message);
    }

    // Update user doc with groupIds
    try {
      const ownerUserRef = doc(db, "users", ownerUid);
      await setDoc(
        ownerUserRef,
        {
          groupIds: arrayUnion(GROUP_ALL_STAFF),
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
    } catch (err) {
      console.warn("[Setup] Could not update user with groupIds:", err.message);
    }
  } catch (err) {
    console.warn("[Setup] Error ensuring system groups (non-critical):", err.message);
  }

  console.log("[Setup] ✅ ensureSystemGroups completed");
};

// ✅ FIXED: Never creates new company for existing users - throws error instead
const getOrCreateCompanyId = async ({ uid }) => {
  console.log("[Setup] getOrCreateCompanyId for uid:", uid);

  const userRef = doc(db, "users", uid);

  try {
    const snap = await getDoc(userRef);

    if (!snap.exists()) {
      console.error("[Setup] ❌ CRITICAL: User doc doesn't exist for uid:", uid);
      throw new Error("User profile not found. Please log out and log back in, or contact support.");
    }

    const data = snap.data();
    const existing = (data?.companyId || "").trim();

    if (existing && existing !== uid) {
      console.log("[Setup] ✅ Found existing companyId:", existing);
      return { companyId: existing, userData: data };
    }

    if (existing === uid) {
      console.error("[Setup] ❌ CRITICAL: companyId equals uid (data corruption detected):", uid);
      throw new Error(
        "Your account has corrupted company data (companyId = uid). " +
          "This is a known bug that has been fixed. " +
          "Please contact support to restore your correct company data. " +
          "DO NOT PROCEED or you will create duplicate data."
      );
    }

    console.error("[Setup] ❌ CRITICAL: Existing user missing companyId:", uid);
    throw new Error(
      "Your account is missing company information. " +
        "Please contact support to restore your correct company data. " +
        "DO NOT PROCEED or you will create duplicate data."
    );
  } catch (err) {
    console.error("[Setup] ❌ Error in getOrCreateCompanyId:", err.message);

    if (err.message.includes("contact support")) throw err;

    throw new Error(
      "Unable to load company information: " +
        err.message +
        ". Please try refreshing the page or contact support if the problem persists."
    );
  }
};

export default function CompleteAccountSetup() {
  const navigate = useNavigate();
  const isNavigatingAway = useRef(false);
  const initRunRef = useRef(false);
  const latestFormRef = useRef(null);
  const latestAdminSeatsRef = useRef(1);
  const latestUserSeatsRef = useRef(1);
  const currentUid = auth.currentUser?.uid || null;
  const rawSnapshot = readSetupSnapshot();
  const initialSnapshot =
    rawSnapshot && currentUid && rawSnapshot.uid === currentUid ? rawSnapshot : null;
  if (rawSnapshot && currentUid && rawSnapshot.uid !== currentUid) {
    clearSetupSnapshot();
  }
  const initialSnapshotUid = initialSnapshot?.uid || null;
  const initialSnapshotForm = useMemo(() => initialSnapshot?.form || {}, [initialSnapshot]);

  // UI state
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [bootstrapping, setBootstrapping] = useState(true);
  const [error, setError] = useState("");

  // Form data
  const [form, setForm] = useState(
    initialSnapshot?.form || {
      fullName: "",
      contactPhone: "",
      companyName: "",
      companyType: "",
      companyEmail: "",
      companyPhone: "",
      addressStreet: "",
      addressCity: "",
      addressState: "",
      addressZip: "",
      specialties: [],
    }
  );

  // ✅ Seat selection (local UI state only)
  const [adminSeats, setAdminSeats] = useState(initialSnapshot?.adminSeats ?? 1); // min 1
  const [userSeats, setUserSeats] = useState(initialSnapshot?.userSeats ?? 1); // default 1 to match your trial expectation; change to 0 if desired

  useEffect(() => {
    latestFormRef.current = form;
    latestAdminSeatsRef.current = adminSeats;
    latestUserSeatsRef.current = userSeats;
  }, [form, adminSeats, userSeats]);

  const handleChange = (field, val) => setForm((prev) => ({ ...prev, [field]: val }));

  const toggleSpecialty = (option) => {
    setForm((prev) => {
      const current = prev.specialties || [];
      const exists = current.includes(option);
      return {
        ...prev,
        specialties: exists ? current.filter((o) => o !== option) : [...current, option],
      };
    });
  };

  const persistSetupSnapshot = useCallback((uid) => {
    if (!uid) return;
    writeSetupSnapshot({
      uid,
      form,
      adminSeats,
      userSeats,
      updatedAt: Date.now(),
    });
  }, [form, adminSeats, userSeats]);

  useEffect(() => {
    const uid = auth.currentUser?.uid || initialSnapshotUid || null;
    if (!uid || bootstrapping || isNavigatingAway.current) return;
    persistSetupSnapshot(uid);
  }, [bootstrapping, initialSnapshotUid, persistSetupSnapshot]);

  // ========================================================
  // INITIAL LOAD — Step selection + Prefill
  // ========================================================
  useEffect(() => {
    if (isNavigatingAway.current || initRunRef.current) return;
    initRunRef.current = true;

    let cancelled = false;

    const init = async () => {
      if (cancelled || isNavigatingAway.current) return;

      setBootstrapping(true);
      console.log("[Setup] 🔄 Init starting. href:", window.location.href);

      try {
        const user = auth.currentUser;
        if (!user) {
          if (!cancelled && !isNavigatingAway.current) navigate("/login");
          return;
        }

        const uid = user.uid;

        const params = new URLSearchParams(window.location.search);
        const urlMode = params.get("mode");
        const isUpgradeFromUrl = urlMode === "upgrade";

        const { companyId, userData } = await getOrCreateCompanyId({ uid });
        if (cancelled || isNavigatingAway.current) return;

        const derivedFullName = deriveFullName({ user, userData });
        const baseFormPatch = {
          fullName: derivedFullName || "",
          contactPhone: userData?.phone || "",
        };
        const hasCompanyInfo = userData?.companySetupCompleted === true;
        const initialStep = hasCompanyInfo ? 2 : 1;

        setForm((prev) => ({
          ...prev,
          fullName: prev.fullName || baseFormPatch.fullName,
          contactPhone: prev.contactPhone || baseFormPatch.contactPhone,
        }));
        setStep(initialStep);
        const currentForm = latestFormRef.current || initialSnapshotForm;
        const currentAdminSeats = latestAdminSeatsRef.current;
        const currentUserSeats = latestUserSeatsRef.current;

        writeSetupSnapshot({
          uid,
          form: {
            ...currentForm,
            fullName: baseFormPatch.fullName || currentForm.fullName,
            contactPhone: baseFormPatch.contactPhone || currentForm.contactPhone || "",
          },
          adminSeats: currentAdminSeats,
          userSeats: currentUserSeats,
          updatedAt: Date.now(),
        });
        setBootstrapping(false);

        await ensureSystemGroups({ companyId, ownerUid: uid });
        if (cancelled || isNavigatingAway.current) return;

        // Prefill company info after the shell is already visible so users never get stuck here.
        try {
          const companyRef = doc(db, ORG_ROOT, companyId);
          const companySnap = await getDoc(companyRef);
          if (cancelled || isNavigatingAway.current) return;

          if (companySnap.exists()) {
            const c = companySnap.data();

            const existingBusinessType = c.businessType || c.companyType || "";
            const normalizedCompanyType = COMPANY_TYPES.includes(existingBusinessType)
              ? existingBusinessType
              : existingBusinessType
              ? "Other"
              : "";

            const candidateName = c.businessName || c.name || "";
            const safeCompanyName = sanitizeCompanyName(candidateName, derivedFullName);

            setForm((prev) => ({
              ...prev,
              fullName: prev.fullName || baseFormPatch.fullName,
              contactPhone: prev.contactPhone || baseFormPatch.contactPhone,
              companyName: safeCompanyName,
              companyType: normalizedCompanyType,
              companyEmail: c.companyEmail || "",
              companyPhone: c.companyPhone || "",
              addressStreet: c.address?.street || "",
              addressCity: c.address?.city || "",
              addressState: c.address?.state || "",
              addressZip: c.address?.zip || "",
              specialties: Array.isArray(c.specialties) ? c.specialties : [],
            }));

            const adminFromSeatLimits = parseSeat(c?.seatLimits?.admin);
            const endFromSeatLimits = parseSeat(c?.seatLimits?.endUser);

            if (adminFromSeatLimits !== null) setAdminSeats(clampSeat(adminFromSeatLimits, 1));
            if (endFromSeatLimits !== null) setUserSeats(clampSeat(endFromSeatLimits, 0));
          }
        } catch (readErr) {
          console.warn("[Setup] ⚠️ Could not read company doc for prefill:", readErr.message);
        }

        if (cancelled || isNavigatingAway.current) return;

        if (isUpgradeFromUrl) console.log("[Setup] ⬆️ Upgrade mode detected → Step 2");
      } catch (err) {
        if (cancelled || isNavigatingAway.current) return;
        console.error("[Setup] ❌ Error during init:", err);
        setError(err.message || "Failed loading setup information.");
      } finally {
        if (!cancelled && !isNavigatingAway.current) {
          setBootstrapping(false);
        }
      }
    };

    init();

    return () => {
      cancelled = true;
    };
  }, [navigate, initialSnapshotForm, initialSnapshotUid]);

  // ========================================================
  // Handle Stripe returning with checkout=success (paid plan)
  // ========================================================
  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    const returnedFromCheckout =
      q.get("checkout") === "success" || !!q.get("session_id");

    if (returnedFromCheckout) {
      finishSetup();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ========================================================
  // Logout
  // ========================================================
  const handleLogout = async () => {
    clearSetupSnapshot();
    await signOut(auth);
    navigate("/login");
  };

  // ========================================================
  // STEP 1 — Save Company Info
  // ========================================================
  const saveCompanyInfo = async () => {
    try {
      setLoading(true);
      setError("");

      const user = auth.currentUser;
      if (!user) throw new Error("Not logged in");
      const uid = user.uid;

      const { companyId } = await getOrCreateCompanyId({ uid });

      const companyRef = doc(db, ORG_ROOT, companyId);
      const companySnap = await getDoc(companyRef);

      const safeCompanyName = sanitizeCompanyName(form.companyName, form.fullName);

      const companyPayload = {
        companyId,
        ownerUid: uid,
        businessName: safeCompanyName,
        name: safeCompanyName,
        businessType: form.companyType || "",
        companyType: form.companyType || "",
        companyTypeOther: form.companyType === "Other" ? "Other" : "",
        companyEmail: form.companyEmail,
        companyPhone: form.companyPhone,
        address: {
          street: form.addressStreet,
          city: form.addressCity,
          state: form.addressState,
          zip: form.addressZip,
        },
        specialties: form.specialties || [],
        specialtiesOther: "",
        onboardingComplete: false,
        defaultVisibility: "groups",
        defaultGroupIds: [GROUP_ALL_STAFF],
        primaryContactName: (form.fullName || "").trim(),
        updatedAt: serverTimestamp(),
      };

      if (!companySnap.exists()) {
        companyPayload.createdAt = serverTimestamp();
        companyPayload.createdBy = uid;
      }

      await setDoc(companyRef, companyPayload, { merge: true });

      const userRef = doc(db, "users", uid);
      await setDoc(
        userRef,
        {
          companyId,
          companySetupCompleted: true,
          fullName: (form.fullName || "").trim(),
          phone: (form.contactPhone || "").trim(),
          groupIds: arrayUnion(GROUP_ALL_STAFF),
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );

      await ensureSystemGroups({ companyId, ownerUid: uid });

      updateLocalUserProfile({
        companyId,
        companySetupCompleted: true,
        fullName: (form.fullName || "").trim(),
        phone: (form.contactPhone || "").trim(),
      });

      writeSetupSnapshot({
        uid,
        form,
        adminSeats,
        userSeats,
        updatedAt: Date.now(),
      });
      setStep(2);
    } catch (err) {
      console.error("[Setup] ❌ Error saving company info:", err);
      setError(err.message || "Could not save company info.");
    }
    setLoading(false);
  };

  // ========================================================
  // Helper: persist seatLimits to company (ONLY seatLimits)
  // ========================================================
  const persistSeatLimits = async ({ companyId, admin, endUser }) => {
    const companyRef = doc(db, ORG_ROOT, companyId);
    await updateDoc(companyRef, {
      seatLimits: {
        admin: clampSeat(admin, 1),
        endUser: clampSeat(endUser, 0),
      },
      updatedAt: serverTimestamp(),
    });
  };

  // ========================================================
  // STEP 2 — Launch Stripe Checkout (paid plan)
  // ========================================================
  const startStripeCheckout = async () => {
    setError("");
    setLoading(true);

    try {
      const user = auth.currentUser;
      if (!user) throw new Error("Not logged in");

      const uid = user.uid;
      const { companyId } = await getOrCreateCompanyId({ uid });

      // ✅ Write seatLimits before redirect so it survives the Stripe roundtrip
      await persistSeatLimits({ companyId, admin: adminSeats, endUser: userSeats });

      const authToken = await user.getIdToken(true);

      const res = await postFunctionJson(
        "https://us-central1-deliveryapp-c1bf2.cloudfunctions.net/createStripeCheckout",
        {
          authToken,
          body: {
            adminSeats: clampSeat(adminSeats, 1),
            endUserSeats: clampSeat(userSeats, 0),
          },
        }
      );

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Checkout failed");
      if (!data.url) throw new Error("Checkout URL missing from server response.");

      window.location.href = data.url;
    } catch (err) {
      console.error("[Setup] ❌ startStripeCheckout error:", err);
      setError(err.message || "Could not start payment.");
      setLoading(false);
    }
  };

  // ========================================================
  // STEP 3 (paid) — Finalize Setup After Stripe
  // ========================================================
  const finishSetup = async () => {
    try {
      setLoading(true);
      setError("");

      const user = auth.currentUser;
      if (!user) throw new Error("Not logged in");

      const uid = user.uid;
      const { companyId } = await getOrCreateCompanyId({ uid });

      await updateDoc(doc(db, "users", uid), {
        companySetupCompleted: true,
        setupCompleted: true,
        subscriptionActive: true,
        planType: "paid",
        updatedAt: serverTimestamp(),
      });

      await updateDoc(doc(db, ORG_ROOT, companyId), {
        onboardingComplete: true,
        planType: "paid",
        updatedAt: serverTimestamp(),
      });

      updateLocalUserProfile({
        setupCompleted: true,
        subscriptionActive: true,
        planType: "paid",
      });
      setPostSetupRouteOverride({
        uid,
        route: "/dashboard",
        planType: "paid",
      });

      clearSetupSnapshot();
      isNavigatingAway.current = true;
      window.location.replace(`${getAppBasePath()}/dashboard`);
    } catch (err) {
      console.error("[Setup] ❌ finishSetup error:", err);
      setError("Payment succeeded but setup failed. Contact support.");
      setLoading(false);
    }
  };

  // ========================================================
  // STEP 2 / TRIAL — Start Free Trial (writes ONLY seatLimits)
  // ========================================================
  const startFreeTrial = async () => {
    try {
      setLoading(true);
      setError("");

      const user = auth.currentUser;
      if (!user) throw new Error("Not logged in");

      const uid = user.uid;
      const { companyId } = await getOrCreateCompanyId({ uid });

      const trialMs = Date.now() + 7 * 24 * 60 * 60 * 1000;
      const trialEndsAt = Timestamp.fromDate(new Date(trialMs));
      const trialEndsIso = new Date(trialMs).toISOString();

      isNavigatingAway.current = true;

      // ✅ Persist seatLimits for trial (ONLY seatLimits)
      await persistSeatLimits({ companyId, admin: adminSeats, endUser: userSeats });

      // Update user document
      await updateDoc(doc(db, "users", uid), {
        companySetupCompleted: true,
        setupCompleted: true,
        subscriptionActive: true,
        planType: "trial",
        trialEndsAt,
        updatedAt: serverTimestamp(),
      });

      // Update company document (NO other seat fields)
      await updateDoc(doc(db, ORG_ROOT, companyId), {
        onboardingComplete: true,
        planType: "trial",
        trialEndsAt,
        updatedAt: serverTimestamp(),
      });

      updateLocalUserProfile({
        setupCompleted: true,
        subscriptionActive: true,
        planType: "trial",
        trialEndsAt: trialEndsIso,
        companySetupCompleted: true,
        companyId,
      });
      setPostSetupRouteOverride({
        uid,
        route: "/dashboard",
        planType: "trial",
        trialEndsAt: trialEndsIso,
      });

      clearSetupSnapshot();
      window.location.replace(`${getAppBasePath()}/dashboard`);
    } catch (err) {
      console.error("[Setup] ❌ startFreeTrial error:", err);
      isNavigatingAway.current = false;
      setError(err.message || "Could not start free trial.");
      setLoading(false);
    }
  };

  // ========================================================
  // RENDERING
  // ========================================================
  const params = new URLSearchParams(window.location.search);
  const isUpgradeMode = params.get("mode") === "upgrade";

  const currentStepLabel = step === 1 ? "Step 1 of 2 · Company details" : "Step 2 of 2 · Seats & plan";

  if (bootstrapping) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-sky-900 via-slate-900 to-slate-950 flex items-center justify-center px-4">
        <div className="w-full max-w-md rounded-3xl border border-white/10 bg-white/10 p-6 backdrop-blur-md shadow-2xl">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full border-2 border-white/40 border-t-white animate-spin" />
            <div>
              <div className="text-sm font-semibold text-white">Preparing your workspace</div>
              <div className="text-xs text-white/70 mt-0.5">Loading your company setup flow…</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-900 via-slate-900 to-slate-950 flex items-center justify-center px-4">
      <div className="max-w-7xl w-full grid grid-cols-1 lg:grid-cols-[1fr_2fr] gap-10 items-start">
        {/* Left side */}
        <div className="text-slate-50 hidden lg:block">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-sky-500/10 border border-sky-400/30 text-xs font-medium mb-4">
            <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
            {isUpgradeMode ? "Move from trial to a predictable, paid plan." : "Lock in your company profile and seat counts."}
          </div>

          <h1 className="text-3xl lg:text-4xl font-semibold mb-4">
            {isUpgradeMode ? (
              <>
                Upgrade your <span className="text-sky-300">SupplyHawk</span> workspace.
              </>
            ) : (
              <>
                Finish setting up your <span className="text-sky-300">SupplyHawk</span> company.
              </>
            )}
          </h1>

          <p className="text-slate-200/80 text-sm leading-relaxed mb-4 max-w-md">
            Define your company, choose seats, and unlock a cleaner way to manage samples, follow-ups, and conversions to orders.
          </p>

          <ul className="space-y-2 text-xs text-slate-200/80">
            <li className="flex items-start gap-2">
              <span className="mt-0.5 w-4 h-4 rounded-full bg-emerald-400/90 flex items-center justify-center text-[10px] text-slate-950 font-bold">✓</span>
              <span>Centralize your company profile and specialties.</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="mt-0.5 w-4 h-4 rounded-full bg-emerald-400/90 flex items-center justify-center text-[10px] text-slate-950 font-bold">✓</span>
              <span>Choose admin and user seats that match your current team size.</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="mt-0.5 w-4 h-4 rounded-full bg-emerald-400/90 flex items-center justify-center text-[10px] text-slate-950 font-bold">✓</span>
              <span>Start on a free trial or move straight into a paid plan.</span>
            </li>
          </ul>
        </div>

        {/* Right side */}
        <div className="bg-white/95 rounded-2xl shadow-2xl shadow-sky-900/40 border border-slate-200/70 backdrop-blur-sm p-6 sm:p-7">
          <div className="flex items-center justify-between mb-2">
            <p className="text-[10px] sm:text-xs text-slate-500">{isUpgradeMode ? "Billing & seats" : currentStepLabel}</p>
            <button
              onClick={handleLogout}
              className="text-[10px] sm:text-xs text-slate-500 hover:text-slate-700 underline-offset-2 hover:underline"
              type="button"
            >
              Log out
            </button>
          </div>

          <h2 className="text-lg sm:text-xl font-semibold text-slate-900 mb-1 text-center">
            {isUpgradeMode
              ? step === 1
                ? "Confirm your company profile"
                : "Upgrade your plan"
              : step === 1
              ? "Complete your company profile"
              : "Choose your seats & plan"}
          </h2>

          <p className="text-[11px] sm:text-xs text-slate-500 mb-4 text-center">
            {isUpgradeMode
              ? step === 1
                ? "If anything changed, update it before billing."
                : "Adjust seats and continue to Stripe to switch to a paid plan."
              : step === 1
              ? "A few details help tailor SupplyHawk to your company."
              : "Pick how many admin and end-user seats you want to start with."}
          </p>

          {error && (
            <div className="mb-3 rounded-md bg-red-50 border border-red-200 px-3 py-2 text-[11px] sm:text-xs text-red-700">
              {error}
            </div>
          )}

          {/* STEP 1 */}
          {step === 1 && (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (!loading) saveCompanyInfo();
              }}
              className="space-y-3"
            >
              <Input label="Full Name" required value={form.fullName} onChange={(v) => handleChange("fullName", v)} />

              <Input
                label="Contact Phone"
                required
                type="tel"
                value={form.contactPhone}
                onChange={(v) => handleChange("contactPhone", v)}
              />

              <Input
                label="Company Name"
                required
                value={form.companyName}
                onChange={(v) => handleChange("companyName", v)}
              />

              <div className="flex flex-col gap-1">
                <div className="flex items-center gap-3">
                  <label className="w-32 text-[11px] font-medium text-slate-800">
                    Company Type <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={form.companyType}
                    onChange={(e) => handleChange("companyType", e.target.value)}
                    required
                    className="flex-1 border border-slate-200 focus:border-sky-400 focus:ring-2 focus:ring-sky-100 outline-none rounded-md px-3 py-1.5 text-xs sm:text-sm bg-white/90"
                  >
                    <option value="">Select a company type</option>
                    {COMPANY_TYPES.map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </select>
                </div>
                <p className="ml-32 text-[10px] text-slate-500">Pick the closest match. Choose "Other" if needed.</p>
              </div>

              <div className="flex flex-col gap-1">
                <div className="flex items-center gap-3">
                  <label className="w-32 text-[11px] font-medium text-slate-800">Main specialties</label>
                  <div className="flex-1">
                    <p className="text-[10px] text-slate-500 mb-1">Select all that apply. Scroll to see the full list.</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2 border border-slate-200/80 rounded-md px-3 py-2 bg-white/60 h-64 overflow-y-auto">
                      {[...COMPANY_SPECIALTIES, "Other"].map((option) => (
                        <label key={option} className="flex items-center gap-2 text-[11px] sm:text-xs text-slate-700">
                          <input
                            type="checkbox"
                            className="h-4 w-4 rounded border-slate-300"
                            checked={form.specialties.includes(option)}
                            onChange={() => toggleSpecialty(option)}
                          />
                          <span>{option}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              <Input label="Company Email" type="email" value={form.companyEmail} onChange={(v) => handleChange("companyEmail", v)} />

              <Input
                label="Company Phone"
                required
                type="tel"
                value={form.companyPhone}
                onChange={(v) => handleChange("companyPhone", v)}
              />

              <Input label="Street" value={form.addressStreet} onChange={(v) => handleChange("addressStreet", v)} />

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <Input label="City" value={form.addressCity} onChange={(v) => handleChange("addressCity", v)} />
                <Input label="State" value={form.addressState} onChange={(v) => handleChange("addressState", v)} />
                <Input label="Zip" value={form.addressZip} onChange={(v) => handleChange("addressZip", v)} />
              </div>

              <button
                className="mt-1 w-full bg-sky-600 hover:bg-sky-700 disabled:bg-sky-400 disabled:cursor-not-allowed text-white py-2 rounded-full text-xs sm:text-sm font-semibold shadow-lg shadow-sky-500/30 transition-colors"
                type="submit"
                disabled={loading}
              >
                {loading ? "Saving company profile..." : "Continue to seats"}
              </button>
            </form>
          )}

          {/* STEP 2 */}
          {step === 2 && (
            <div className="space-y-4">
              <SeatPicker
                label="Admin seats"
                description="Sales leadership, super-admins, ops managers."
                value={adminSeats}
                setValue={setAdminSeats}
                min={1}
              />
              <SeatPicker
                label="User seats"
                description="Sales reps and anyone managing samples."
                value={userSeats}
                setValue={setUserSeats}
                min={0}
              />

              <div className="bg-slate-50 border border-slate-200 rounded-xl px-4 py-3">
                <p className="text-xs sm:text-sm text-slate-700">
                  Admin seats: <strong>{adminSeats}</strong>
                </p>
                <p className="text-xs sm:text-sm text-slate-700">
                  User seats: <strong>{userSeats}</strong>
                </p>
                <p className="font-semibold mt-2 text-slate-900 text-sm">
                  Total:{" "}
                  <span className="text-sky-700">
                    ${clampSeat(adminSeats, 1) * 18 + clampSeat(userSeats, 0) * 12}/month
                  </span>
                </p>
              </div>

              <button
                type="button"
                onClick={startStripeCheckout}
                disabled={loading}
                className="w-full bg-emerald-500 hover:bg-emerald-600 disabled:bg-emerald-400 disabled:cursor-not-allowed text-white py-2 rounded-full text-xs sm:text-sm font-semibold shadow-lg shadow-emerald-500/30 transition-colors"
              >
                {loading ? "Redirecting to checkout..." : "Review pricing & pay"}
              </button>

              {!new URLSearchParams(window.location.search).get("mode") && (
                <>
                  <div className="text-center text-[10px] sm:text-xs my-1 text-slate-500">or</div>

                  <button
                    type="button"
                    onClick={startFreeTrial}
                    disabled={loading}
                    className="w-full border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-60 disabled:cursor-not-allowed py-2 rounded-full text-xs sm:text-sm font-semibold transition-colors"
                  >
                    {loading ? "Starting trial..." : "Start 7-day free trial"}
                  </button>

                  <button
                    onClick={() => setStep(1)}
                    type="button"
                    className="block w-full text-center text-[10px] sm:text-xs text-slate-500 hover:text-slate-700 mt-1 underline-offset-2 hover:underline"
                  >
                    ← Back to company details
                  </button>
                </>
              )}

              {new URLSearchParams(window.location.search).get("mode") === "upgrade" && (
                <button
                  type="button"
                  onClick={() => navigate("/dashboard")}
                  className="w-full border border-slate-300 text-slate-700 hover:bg-slate-50 py-2 rounded-full text-xs sm:text-sm font-semibold transition-colors mt-1"
                >
                  Keep current plan / Back to app
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// SIMPLE INPUT COMPONENT
function Input({ label, value, onChange, required, type = "text" }) {
  return (
    <div className="flex items-center gap-3">
      <label className="w-32 text-[11px] font-medium text-slate-800">
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>
      <input
        type={type}
        required={required}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="flex-1 border border-slate-200 focus:border-sky-400 focus:ring-2 focus:ring-sky-100 outline-none rounded-md px-3 py-1.5 text-xs sm:text-sm bg-white/90"
      />
    </div>
  );
}

// SEAT PICKER
function SeatPicker({ label, description, value, setValue, min }) {
  return (
    <div className="p-3 rounded-xl border border-slate-200/80 bg-slate-50/80">
      <div className="flex justify-between items-baseline mb-1">
        <h3 className="font-medium text-xs sm:text-sm text-slate-900">{label}</h3>
      </div>
      {description && <p className="text-[10px] sm:text-xs text-slate-500 mb-2">{description}</p>}
      <div className="flex items-center gap-3">
        <button
          className="px-3 py-1 rounded-full bg-slate-200 hover:bg-slate-300 text-slate-800 text-xs font-medium disabled:opacity-60 disabled:cursor-not-allowed"
          onClick={() => setValue((v) => Math.max(min, v - 1))}
          type="button"
          disabled={value <= min}
        >
          −
        </button>
        <span className="text-lg sm:text-xl font-semibold text-slate-900">{value}</span>
        <button
          className="px-3 py-1 rounded-full bg-slate-200 hover:bg-slate-300 text-slate-800 text-xs font-medium"
          onClick={() => setValue((v) => v + 1)}
          type="button"
        >
          +
        </button>
      </div>
    </div>
  );
}
